/**
 * Photo Storage Utility
 * Handles photo upload, validation, and storage with size limits
 * 
 * Storage Priority:
 * 1. Backend API (primary - syncs across devices)
 * 2. IndexedDB (cache/offline)
 * 3. localStorage (fallback if IndexedDB unavailable)
 */

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_DIMENSION = 2000; // 2000px
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const WARN_SIZE = 1 * 1024 * 1024; // 1MB - warn but allow

/**
 * Validate photo file
 * @param {File} file - Image file to validate
 * @returns {Promise<{valid: boolean, error?: string, warning?: string, file: File}>}
 */
export async function validatePhoto(file) {
  // Check file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Only JPG, PNG, and WebP images are allowed',
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'Image size must be less than 2MB',
    };
  }

  // Check dimensions and resize if needed
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const needsResize = img.width > MAX_DIMENSION || img.height > MAX_DIMENSION;
      const warning = file.size > WARN_SIZE ? 'Large file size. Consider compressing the image.' : null;

      if (needsResize) {
        // Auto-resize maintaining aspect ratio
        resizeImage(img, MAX_DIMENSION)
          .then((resizedFile) => {
            resolve({
              valid: true,
              file: resizedFile,
              warning: warning || 'Image was resized to fit maximum dimensions',
            });
          })
          .catch((err) => {
            reject(new Error(`Failed to resize image: ${err.message}`));
          });
      } else {
        resolve({
          valid: true,
          file: file,
          warning: warning,
        });
      }
    };
    img.onerror = () => {
      reject(new Error('Invalid image file'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Resize image maintaining aspect ratio
 * @param {HTMLImageElement} img - Image element
 * @param {number} maxDimension - Maximum width or height
 * @returns {Promise<File>}
 */
function resizeImage(img, maxDimension) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    let width = img.width;
    let height = img.height;

    // Calculate new dimensions maintaining aspect ratio
    if (width > height) {
      if (width > maxDimension) {
        height = (height * maxDimension) / width;
        width = maxDimension;
      }
    } else {
      if (height > maxDimension) {
        width = (width * maxDimension) / height;
        height = maxDimension;
      }
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to resize image'));
          return;
        }
        const resizedFile = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
        resolve(resizedFile);
      },
      'image/jpeg',
      0.9 // Quality
    );
  });
}

/**
 * Get photo storage key for user
 * @param {string|number} userId - User ID
 * @returns {string}
 */
function getPhotoKey(userId) {
  return `teacher_profile_photo_${userId}`;
}

/**
 * Store photo in IndexedDB
 * @param {string|number} userId - User ID
 * @param {File|Blob} photo - Photo file
 * @returns {Promise<void>}
 */
export async function storePhotoLocal(userId, photo) {
  const key = getPhotoKey(userId);

  // Try IndexedDB first
  if ('indexedDB' in window) {
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      await new Promise((resolve, reject) => {
        const request = store.put({ id: key, photo: photo, timestamp: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
      return;
    } catch (error) {
      console.warn('IndexedDB storage failed, falling back to localStorage:', error);
    }
  }

  // Fallback to localStorage (convert to base64)
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        localStorage.setItem(key, reader.result);
        localStorage.setItem(`${key}_timestamp`, Date.now().toString());
        resolve();
      };
      reader.onerror = () => reject(new Error('Failed to read photo file'));
      reader.readAsDataURL(photo);
    } catch (error) {
      console.error('LocalStorage storage failed:', error);
      reject(new Error('Failed to store photo locally'));
    }
  });
}

/**
 * Get photo from local storage
 * @param {string|number} userId - User ID
 * @returns {Promise<string|null>} - Data URL or null
 */
export async function getPhotoLocal(userId) {
  const key = getPhotoKey(userId);

  // Try IndexedDB first
  if ('indexedDB' in window) {
    try {
      const db = await openIndexedDB();
      const transaction = db.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const result = await new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      if (result && result.photo) {
        // Convert Blob to data URL
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(result.photo);
        });
      }
    } catch (error) {
      console.warn('IndexedDB retrieval failed, trying localStorage:', error);
    }
  }

  // Fallback to localStorage
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('LocalStorage retrieval failed:', error);
    return null;
  }
}

/**
 * Open IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
function openIndexedDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('TeacherAppStorage', 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('photos')) {
        db.createObjectStore('photos', { keyPath: 'id' });
      }
    };
  });
}

/**
 * Upload photo to backend API
 * @param {string|number} userId - User ID
 * @param {File} photo - Photo file
 * @param {string} token - Auth token
 * @returns {Promise<void>}
 */
export async function uploadPhotoToBackend(userId, photo, token) {
  try {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('user_id', userId);

    const response = await fetch('/api/teacher/profile/photo', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Backend upload failed:', error);
    throw error;
  }
}

/**
 * Get photo from backend API
 * @param {string|number} userId - User ID
 * @param {string} token - Auth token
 * @returns {Promise<string|null>} - Photo URL or null
 */
export async function getPhotoFromBackend(userId, token) {
  try {
    const response = await fetch(`/api/teacher/profile/photo?user_id=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No photo uploaded yet
      }
      throw new Error(`Failed to fetch photo: ${response.statusText}`);
    }

    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Backend photo fetch failed:', error);
    return null;
  }
}

